/**
 * Tour Tooltip Component
 *
 * Displays step content with title, description, and navigation buttons.
 * Positioned relative to the current tour target element.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Navigation,
  Brain,
  Zap,
  Wind,
  BarChart3,
  Heart,
  Rocket,
  type LucideIcon,
} from 'lucide-react';
import { useTour } from './TourProvider';

// Icon map for step icons
const ICON_MAP: Record<string, LucideIcon> = {
  Sparkles,
  Navigation,
  Brain,
  Zap,
  Wind,
  BarChart3,
  Heart,
  Rocket,
};

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition?: 'top' | 'bottom' | 'left' | 'right';
}

export function TourTooltip() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isLastStep,
    interactionRequired,
    getTargetRect,
    nextStep,
    prevStep,
    skipTour,
  } = useTour();

  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });

  // Calculate tooltip position based on target and placement
  const calculatePosition = useCallback(() => {
    if (!currentStep) return;

    const tooltipWidth = 320;
    // Account for header, content, interaction hint, buttons, and progress bar
    const tooltipHeight = interactionRequired ? 300 : 260;
    const margin = 16;
    const viewportPadding = 16;

    // Center placement - center of screen (slightly above middle for better visibility)
    if (currentStep.placement === 'center') {
      setPosition({
        top: Math.max(viewportPadding, (window.innerHeight - tooltipHeight) / 2 - 50),
        left: Math.max(viewportPadding, (window.innerWidth - tooltipWidth) / 2),
      });
      return;
    }

    const rect = getTargetRect(currentStep.targetId);
    if (!rect) {
      // Fallback to center
      setPosition({
        top: Math.max(viewportPadding, (window.innerHeight - tooltipHeight) / 2 - 50),
        left: Math.max(viewportPadding, (window.innerWidth - tooltipWidth) / 2),
      });
      return;
    }

    let top = 0;
    let left = 0;
    let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

    // Calculate available space
    const spaceAbove = rect.top - viewportPadding;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;

    // Determine best placement based on available space
    let effectivePlacement = currentStep.placement;

    // If preferred placement is 'bottom' but not enough space below, try top
    if (effectivePlacement === 'bottom' && spaceBelow < tooltipHeight + margin) {
      if (spaceAbove >= tooltipHeight + margin) {
        effectivePlacement = 'top';
      }
    }

    switch (effectivePlacement) {
      case 'top':
        // Position above the target
        top = rect.top - tooltipHeight - margin;
        // If tooltip would go above viewport, position it at the top
        if (top < viewportPadding) {
          top = viewportPadding;
        }
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'bottom';
        break;
      case 'bottom':
        top = rect.bottom + margin;
        // If tooltip would go below viewport, position it higher
        if (top + tooltipHeight > window.innerHeight - viewportPadding) {
          top = window.innerHeight - tooltipHeight - viewportPadding;
        }
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'top';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - margin;
        arrowPosition = 'right';
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + margin;
        arrowPosition = 'left';
        break;
    }

    // Ensure horizontal bounds
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipWidth - viewportPadding));

    // Ensure vertical bounds - tooltip must be fully visible
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - tooltipHeight - viewportPadding));

    setPosition({ top, left, arrowPosition });
  }, [currentStep, getTargetRect, interactionRequired]);

  useEffect(() => {
    calculatePosition();

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);

    const interval = setInterval(calculatePosition, 100);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
      clearInterval(interval);
    };
  }, [calculatePosition]);

  if (!isActive || !currentStep) return null;

  const IconComponent = currentStep.icon ? ICON_MAP[currentStep.icon] : null;
  // Always allow proceeding - interaction hints are optional guidance, not requirements
  const canProceed = true;

  const content = (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentStep.id}
        className="tour-tooltip"
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: 320,
          zIndex: 252,
          background: 'var(--bg-secondary)',
          border: '3px solid var(--border-harsh)',
          boxShadow: '4px 4px 0 var(--border-harsh)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Header with step counter and skip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '2px solid var(--border-harsh)',
            background: 'var(--bg-primary)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '12px',
              color: 'var(--text-muted)',
              fontWeight: 600,
            }}
          >
            Step {currentStepIndex + 1} of {totalSteps}
          </span>
          <button
            onClick={skipTour}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            Skip Tour
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Icon and Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            {IconComponent && (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  background: currentStep.accentColor || 'var(--accent-growth)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconComponent size={20} className="tour-tooltip-icon" />
              </div>
            )}
            <h3
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}
            >
              {currentStep.title}
            </h3>
          </div>

          {/* Description */}
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '14px',
              lineHeight: 1.5,
              color: 'var(--text-secondary)',
              margin: '0 0 16px 0',
            }}
          >
            {currentStep.description}
          </p>


          {/* Navigation buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {currentStepIndex > 0 && (
              <button
                onClick={prevStep}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '10px 16px',
                  background: 'var(--bg-tertiary)',
                  border: '2px solid var(--border-harsh)',
                  borderRadius: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <ChevronLeft size={16} />
                Back
              </button>
            )}

            <button
              onClick={nextStep}
              disabled={!canProceed}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '10px 16px',
                background: canProceed ? 'var(--accent-growth)' : 'var(--bg-tertiary)',
                border: '2px solid var(--border-harsh)',
                boxShadow: canProceed ? '2px 2px 0 var(--border-harsh)' : 'none',
                borderRadius: '6px',
                color: canProceed ? 'var(--bg-primary)' : 'var(--text-muted)',
                fontSize: '14px',
                fontWeight: 700,
                cursor: canProceed ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
              }}
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {!isLastStep && <ChevronRight size={16} />}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 4,
            background: 'var(--bg-tertiary)',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.3 }}
            style={{
              height: '100%',
              background: 'var(--accent-growth)',
            }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
